/**
 * LCList Component
 *
 * List view for liquid cultures with filtering, sorting, and status management.
 * Shows both real LCs and requirements from shadow spawn batches.
 * Part of US-P2-001: Liquid Culture Management
 */

import { useState, useMemo, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import SearchInput from '@/components/ui/search-input'
import { useLiquidCultures, useStrains } from '@/hooks/useLC'
import { useLCMutations } from '@/hooks/useLCMutations'
import { useShadowLCRequirements } from '@/hooks/useShadowLCRequirements'
import LCStatusBadge, { GenerationBadge } from './LCStatusBadge'
import LCForm from './LCForm'
import LCDetail from './LCDetail'
import SyringesModal from './SyringesModal'
import { parseDateString } from '@/utils/dateUtils'
import {
  Plus,
  Beaker,
  AlertTriangle,
  ChevronRight,
  Filter,
  MoreHorizontal,
  Syringe
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

// Status options for dropdown
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'incubating', label: 'Incubating' },
  { value: 'ready', label: 'Ready' },
  { value: 'in_use', label: 'In Use' },
  { value: 'finished', label: 'Finished' },
  { value: 'contaminated', label: 'Contaminated' }
]

// Status transition options
const statusTransitions = {
  incubating: ['ready', 'contaminated'],
  ready: ['in_use', 'contaminated'],
  in_use: ['finished', 'contaminated'],
  finished: [],
  contaminated: []
}

export default function LCList({ user, onLogout }) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [strainFilter, setStrainFilter] = useState('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingLC, setEditingLC] = useState(null)
  const [selectedLC, setSelectedLC] = useState(null)
  const [selectedLCs, setSelectedLCs] = useState(new Set())
  const [isSyringesModalOpen, setIsSyringesModalOpen] = useState(false)

  const { liquidCultures, loading, error, refetch } = useLiquidCultures()
  const { strains } = useStrains()
  const { updateStatus } = useLCMutations()
  const {
    pendingBatchCounts,
    strainsMissingLC,
    loading: requirementsLoading
  } = useShadowLCRequirements()

  // Auto-select LC from URL param (e.g., /production/lc?select=LC-001)
  useEffect(() => {
    const selectCode = searchParams.get('select')
    if (selectCode && liquidCultures.length > 0 && !selectedLC) {
      const lc = liquidCultures.find(l => l.culture_code === selectCode)
      if (lc) {
        setSelectedLC(lc)
        // Clear the param after selecting
        searchParams.delete('select')
        setSearchParams(searchParams, { replace: true })
      }
    }
  }, [searchParams, liquidCultures, selectedLC, setSearchParams])

  // Check for G3 alert (any G3 cultures exist)
  const hasG3Cultures = useMemo(
    () => liquidCultures.some(lc => lc.generation === 3 && lc.status !== 'contaminated'),
    [liquidCultures]
  )

  // Filter and search logic
  const filteredCultures = useMemo(() => {
    let result = [...liquidCultures]

    // Status filter
    if (statusFilter !== 'all') {
      result = result.filter(lc => lc.status === statusFilter)
    }

    // Strain filter
    if (strainFilter !== 'all') {
      result = result.filter(lc => lc.strain_id === strainFilter)
    }

    // Search
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase()
      result = result.filter(lc =>
        lc.culture_code?.toLowerCase().includes(search) ||
        lc.strain?.name?.toLowerCase().includes(search) ||
        lc.storage_location?.toLowerCase().includes(search)
      )
    }

    return result
  }, [liquidCultures, statusFilter, strainFilter, searchTerm])

  // Selection handlers
  const handleLCSelect = (lcId) => {
    setSelectedLCs(prev => {
      const next = new Set(prev)
      if (next.has(lcId)) {
        next.delete(lcId)
      } else {
        next.add(lcId)
      }
      return next
    })
  }

  const handleSelectAll = () => {
    const lcIds = filteredCultures.map(lc => lc.id)
    if (selectedLCs.size === lcIds.length) {
      setSelectedLCs(new Set())
    } else {
      setSelectedLCs(new Set(lcIds))
    }
  }

  const handleBulkStatusChange = async (newStatus) => {
    try {
      const promises = Array.from(selectedLCs).map(id =>
        updateStatus(id, newStatus)
      )
      await Promise.all(promises)
      setSelectedLCs(new Set())
      refetch()
    } catch (err) {
      console.error('Bulk status change failed:', err)
    }
  }

  const handleAddLC = () => {
    setEditingLC(null)
    setIsFormOpen(true)
  }

  const handleEditLC = (lc) => {
    setEditingLC(lc)
    setIsFormOpen(true)
  }

  const handleViewLC = (lc) => {
    setSelectedLC(lc)
  }

  const handleStatusChange = async (lc, newStatus) => {
    try {
      await updateStatus(lc.id, newStatus)
      refetch()
    } catch (error) {
      // Error handled in mutation
    }
  }

  const handleFormSave = async () => {
    refetch()
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return parseDateString(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const calculateDaysUntilReady = (readyDate) => {
    if (!readyDate) return null
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const ready = new Date(readyDate)
    ready.setHours(0, 0, 0, 0)
    return Math.ceil((ready - today) / (1000 * 60 * 60 * 24))
  }

  const isLoading = loading || requirementsLoading
  const selectedCount = selectedLCs.size

  if (isLoading) {
    return (
      <PageLayout
        title="Liquid Cultures"
        user={user}
        onLogout={onLogout}
      >
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-2 border-muted-foreground border-t-transparent rounded-full animate-spin" />
          <p className="ml-3 text-muted-foreground">Loading liquid cultures...</p>
        </div>
      </PageLayout>
    )
  }

  // Header actions - filters on left, search on right
  const headerActions = (
    <div className="flex items-center gap-2">
      <Select value={statusFilter} onValueChange={setStatusFilter}>
        <SelectTrigger className="w-40">
          <Filter className="h-4 w-4 mr-2" />
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {STATUS_OPTIONS.map(opt => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={strainFilter} onValueChange={setStrainFilter}>
        <SelectTrigger className="w-40">
          <SelectValue placeholder="All Strains" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Strains</SelectItem>
          {strains.map(strain => (
            <SelectItem key={strain.id} value={strain.id}>
              {strain.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="w-64">
        <SearchInput
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search..."
        />
      </div>

      <Button onClick={() => setIsSyringesModalOpen(true)} variant="outline">
        <Syringe className="h-4 w-4 mr-2" />
        Syringes
      </Button>

      <Button onClick={handleAddLC} variant="create">
        <Plus className="h-4 w-4 mr-2" />
        Add LC
      </Button>
    </div>
  )

  return (
    <PageLayout
      title="Liquid Cultures"
      user={user}
      onLogout={onLogout}
      headerActions={headerActions}
    >
      <div className="h-full overflow-y-auto">
      {/* LC Requirements Alert - strains missing LC */}
      {strainsMissingLC.length > 0 && (
        <Alert className="mb-4 border-amber-500 bg-amber-50">
          <AlertTriangle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-amber-800">
            <span className="font-medium">LC Required:</span>{' '}
            {strainsMissingLC.map(r =>
              `${r.strain_name} (${r.batch_count} batches, ${r.total_blocks} blocks)`
            ).join(', ')}
          </AlertDescription>
        </Alert>
      )}

      {/* G3 Alert Banner */}
      {hasG3Cultures && (
        <Alert className="mb-4 border-orange-200 bg-orange-50">
          <AlertTriangle className="h-4 w-4 text-orange-600" />
          <AlertDescription className="text-orange-700">
            <strong>G3 cultures detected.</strong> Consider creating fresh G1 master cultures
            to maintain strain vitality and genetic stability.
          </AlertDescription>
        </Alert>
      )}

      {/* Selection action bar - shown when LCs are selected */}
      {selectedCount > 0 && (
        <div className="mb-4 p-3 bg-odin-blue/10 border border-odin-blue rounded-lg flex items-center justify-between">
          <span className="text-sm font-medium">
            {selectedCount} LC{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelectedLCs(new Set())}>
              Clear Selection
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  Change Status
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('ready')}>
                  Mark as Ready
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('in_use')}>
                  Mark as In Use
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleBulkStatusChange('finished')}>
                  Mark as Finished
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => handleBulkStatusChange('contaminated')}
                  className="text-red-600"
                >
                  Mark as Contaminated
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      )}

      {/* Results count */}
      <div className="text-sm text-content-secondary mb-4">
        Showing {filteredCultures.length} of {liquidCultures.length} liquid cultures
      </div>

      {error && (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {filteredCultures.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Beaker className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No liquid cultures found</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || statusFilter !== 'all' || strainFilter !== 'all'
              ? 'Try adjusting your filters'
              : 'Start by creating your first liquid culture'
            }
          </p>
          {liquidCultures.length === 0 && (
            <Button variant="outline" onClick={handleAddLC}>
              <Plus className="h-4 w-4 mr-2" />
              Create First LC
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <Card className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <Checkbox
                      checked={selectedLCs.size === filteredCultures.length && filteredCultures.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Culture Code</TableHead>
                  <TableHead>Strain</TableHead>
                  <TableHead>Gen</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Ready Date</TableHead>
                  <TableHead>Volume</TableHead>
                  <TableHead>Pending</TableHead>
                  <TableHead className="w-10"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCultures.map(lc => {
                  const daysUntil = calculateDaysUntilReady(lc.ready_date)
                  const availableTransitions = statusTransitions[lc.status] || []
                  const pendingCount = pendingBatchCounts[lc.strain_id] || 0

                  return (
                    <TableRow
                      key={lc.id}
                      className="cursor-pointer"
                      onClick={() => handleViewLC(lc)}
                    >
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedLCs.has(lc.id)}
                          onCheckedChange={() => handleLCSelect(lc.id)}
                        />
                      </TableCell>
                      <TableCell className="font-mono font-semibold">
                        {lc.culture_code}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="font-mono">
                            {lc.strain?.code}
                          </Badge>
                          <span className="text-sm">{lc.strain?.name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <GenerationBadge generation={lc.generation} />
                      </TableCell>
                      <TableCell>
                        <LCStatusBadge status={lc.status} />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p>{formatDate(lc.ready_date)}</p>
                          {lc.status === 'incubating' && daysUntil !== null && (
                            <p className={`text-xs ${daysUntil <= 0 ? 'text-emerald-600 font-medium' : 'text-muted-foreground'}`}>
                              {daysUntil > 0
                                ? `${daysUntil} days`
                                : daysUntil === 0
                                  ? 'Ready today'
                                  : `${Math.abs(daysUntil)}d overdue`
                              }
                            </p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{lc.volume_ml} ml</TableCell>
                      <TableCell>
                        {pendingCount > 0 && (lc.status === 'ready' || lc.status === 'in_use') ? (
                          <Badge variant="outline" className="border-amber-500 text-amber-700 bg-amber-50">
                            {pendingCount} waiting
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewLC(lc)}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditLC(lc)}>
                              Edit
                            </DropdownMenuItem>
                            {availableTransitions.length > 0 && (
                              <>
                                <DropdownMenuSeparator />
                                {availableTransitions.map(status => (
                                  <DropdownMenuItem
                                    key={status}
                                    onClick={() => handleStatusChange(lc, status)}
                                    className={status === 'contaminated' ? 'text-red-600' : ''}
                                  >
                                    Mark as {status.replace('_', ' ')}
                                  </DropdownMenuItem>
                                ))}
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </Card>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filteredCultures.map(lc => {
              const daysUntil = calculateDaysUntilReady(lc.ready_date)
              const pendingCount = pendingBatchCounts[lc.strain_id] || 0

              return (
                <Card
                  key={lc.id}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => handleViewLC(lc)}
                >
                  <CardContent className="py-3 px-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedLCs.has(lc.id)}
                          onCheckedChange={(e) => {
                            e.stopPropagation()
                            handleLCSelect(lc.id)
                          }}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div>
                          <p className="font-mono font-semibold">{lc.culture_code}</p>
                          <p className="text-sm text-muted-foreground">
                            {lc.strain?.name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <GenerationBadge generation={lc.generation} />
                        <LCStatusBadge status={lc.status} showIcon={false} />
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">
                          {formatDate(lc.ready_date)}
                          {lc.status === 'incubating' && daysUntil !== null && (
                            <span className={daysUntil <= 0 ? 'text-emerald-600 ml-1' : 'ml-1'}>
                              ({daysUntil > 0 ? `${daysUntil}d` : 'Ready'})
                            </span>
                          )}
                        </span>
                        {pendingCount > 0 && (lc.status === 'ready' || lc.status === 'in_use') && (
                          <Badge variant="outline" className="border-amber-500 text-amber-700 text-xs">
                            {pendingCount} waiting
                          </Badge>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </>
      )}
      </div>

      {/* LC Form Modal */}
      <LCForm
        isOpen={isFormOpen}
        onClose={() => {
          setIsFormOpen(false)
          setEditingLC(null)
        }}
        onSave={handleFormSave}
        editingLC={editingLC}
      />

      {/* LC Detail Modal */}
      <LCDetail
        isOpen={!!selectedLC}
        onClose={() => setSelectedLC(null)}
        liquidCultureId={selectedLC?.id}
        onEdit={(lc) => {
          setSelectedLC(null)
          handleEditLC(lc)
        }}
        onStatusChange={(lc, status) => {
          handleStatusChange(lc, status)
        }}
        onRefresh={refetch}
      />

      {/* Syringes Modal */}
      <SyringesModal
        open={isSyringesModalOpen}
        onOpenChange={setIsSyringesModalOpen}
      />
    </PageLayout>
  )
}
